import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/style-report.css";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import axios from "axios";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {Card} from "../../../components/card/card.jsx";
import {subMonths} from "date-fns";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import Filters from "./components/filters.jsx";
import Content from "./components/content.jsx";

const API_URL = import.meta.env.VITE_API_URL;

const initialFilters = () => ({
    dtStart: subMonths(new Date(), 2),
    dtEnd: new Date(),
    userLogin: [],
    userName: [],
    userCpf: []
});

const filterLabels = {
    dtStart: "Data/hora inicial",
    dtEnd: "Data/hora final",
    userLogin: "Login",
    userName: "Nome",
    userCpf: "CPF"
};

const initialTableColumns = {
    login: {label: "Agente", visible: true},
    date_time: {label: "D/H exportação", visible: true},
    type: {label: "Tipo", visible: true},
    watermark: {label: "Marca d'água", visible: true},
    plate: {label: "Placa", visible: true},
    capture_time: {label: "D/H passagem", visible: true},
    camera: {label: "Câmera", visible: true},
    city: {label: "Município", visible: true},
};

const ImageExport = () => {
    const [loading, setLoading] = useState(false);

    const [collapsed, setCollapsed] = useState(false);
    const buttonRef = useRef(null);
    const [fixedTop, setFixedTop] = useState(null);

    const [filters, setFilters] = useState(initialFilters);
    const [reportData, setReportData] = useState(null);

    const [title] = useState("Auditoria de exportação de imagens");
    const [headerSelected, setHeaderSelected] = useState(-1);
    const [tableColumns, setTableColumns] = useState(initialTableColumns);

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : ""
            }, filterLabels)
        );

    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        try {
            const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd, 2, "months");

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setLoading(false);
                return;
            }

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
                userLogin: filters.userLogin?.map(i => i.value) ?? [],
                userName: filters.userName?.map(i => i.value) ?? [],
                userCpf: filters.userCpf?.map(i => i.value) ?? []
            };

            const auditFilters = ReportUtils.filters({
                    ...filters,
                    dtStart: formattedFilters.dtStart,
                    dtEnd: formattedFilters.dtEnd},
                filterLabels, true);

            const { data: response } = await axios.post(API_URL + "/image_export/apply", {
                ...formattedFilters,
                filters: auditFilters
            });

            const data = response.data.map(data => ({
                ...data,
                payload: typeof data.payload === "string" ? JSON.parse(data.payload) : data.payload
            }));

            setReportData(data);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {

            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.error("Erro ao gerar o relatório", error);

        } finally {
            setLoading(false);
        }
    };

    useGlobalKey("Enter", apply);

    const clearSearch = () => {
        setFilters(initialFilters);
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }

        setCollapsed(prev => !prev);
    }

    const printComponentRef = useRef(null);

    return (
        <div className={"flex"}>

            <Template isLoading={loading}>
                <Template.Header>
                    <div className="flex justify-between w-full">
                        <Template.Title>{title}</Template.Title>
                        <HeaderButtons
                            exportable={true}
                            exportInSheets={true}
                            printable={true}
                            headerChange={true}
                            visibilityColumns={true}
                            columns={tableColumns}
                            setColumns={setTableColumns}
                            printComponentRef={printComponentRef}
                            reportData={reportData}
                            data={reportData?.length}
                            headerSelected={headerSelected}
                            setHeaderSelected={setHeaderSelected}
                        />
                    </div>
                </Template.Header>
                <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
                    <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                          collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

                        <Filters setFilters={setFilters} filters={filters}/>
                        <AutoDatetime setFilters={setFilters}/>

                    </Menu>
                </Template.Body>
            </Template>

            <Card className={`w-full h-fit mr-2 ${!reportData || reportData?.length === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || reportData?.length === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                <Card.Body>
                    <div className={`flex-1 ${!reportData || reportData?.length === 0 ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
                        <NotAppliedContent reportData={reportData}/>
                        <NoDataContent reportData={reportData} data={reportData?.length}/>
                        <Content reportData={reportData} data={reportData?.length} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef} tableColumns={tableColumns}/>
                    </div>
                </Card.Body>
            </Card>

        </div>
    );
};

export default ImageExport;